import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { toast } from "sonner";
import { KeyRound, Copy, Trash2, Zap, Activity, Plus, Phone, Mail, Globe, Loader2, CheckCircle2, Circle, Sparkles } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiError } from "@/lib/api";
import UsageChart from "@/components/UsageChart";
import BulkCsv from "@/components/BulkCsv";
import ReferralCard from "@/components/ReferralCard";

const KIND_ICON = { phone: Phone, email: Mail, ip: Globe };
const KIND_COLOR = { phone: "text-cyan-signal", email: "text-emerald-400", ip: "text-purple-400" };

export default function Dashboard() {
  const { user, refresh, loading } = useAuth();
  const nav = useNavigate();
  const [lookups, setLookups] = useState([]);
  const [stats, setStats] = useState(null);
  const [newKey, setNewKey] = useState(null);
  const [generating, setGenerating] = useState(false);

  useEffect(() => {
    if (!loading && !user) nav("/login");
  }, [user, loading, nav]);

  const loadHistory = () => {
    api.get("/lookups?limit=20").then(({ data }) => setLookups(data.items || [])).catch(() => {});
    api.get("/stats").then(({ data }) => setStats(data)).catch(() => {});
  };

  useEffect(() => { if (user) loadHistory(); }, [user]);

  if (loading || !user) return <div className="min-h-[60vh] flex items-center justify-center"><Loader2 className="w-6 h-6 animate-spin text-cyan-signal" /></div>;

  const generateKey = async () => {
    setGenerating(true);
    try {
      const { data } = await api.post("/keys/generate");
      setNewKey(data.key);
      await refresh();
      toast.success("API key generated — copy it now, we won't show it again");
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setGenerating(false); }
  };

  const deleteKey = async (id) => {
    try {
      await api.delete(`/keys/${id}`);
      await refresh();
      toast.success("Key revoked");
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const copy = (v) => { navigator.clipboard.writeText(v); toast.success("Copied to clipboard"); };

  const creditsPct = Math.round(((user.credits || 0) / Math.max(user.credits_total || 1, 1)) * 100);

  // Onboarding checklist
  const hasKey = (user.api_keys || []).length > 0;
  const hasLookup = (stats?.total || 0) > 0;
  const hasPromo = user.launch_promo_claimed;
  const isPaid = user.plan && user.plan !== "starter";
  const steps = [
    { done: hasKey,    label: "Generate your first API key",   cta: "Generate", action: generateKey },
    { done: hasLookup, label: "Run your first verification",   cta: "Open Console", to: "/console" },
    { done: hasPromo,  label: "Claim launch bonus (LAUNCH50)", cta: "Claim", to: "/launch" },
    { done: isPaid,    label: "Upgrade to a paid plan",        cta: "See plans", to: "/pricing" },
  ];
  const doneCount = steps.filter((s) => s.done).length;

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 mb-8">
        <div>
          <span className="font-mono text-xs uppercase tracking-widest text-cyan-signal">Dashboard</span>
          <h1 className="text-3xl sm:text-4xl font-display font-bold tracking-tight mt-1">Hey, {user.name || user.email.split("@")[0]}.</h1>
        </div>
        <div className="flex gap-2">
          {user.is_admin && <Link to="/admin"><Button variant="outline" className="border-slate-700 bg-slate-900/50 hover:bg-slate-800" data-testid="btn-open-admin"><Sparkles className="w-4 h-4 mr-2" /> Founder view</Button></Link>}
          <Link to="/console"><Button className="bg-cyan-signal text-slate-950 hover:bg-cyan-400 font-semibold" data-testid="btn-open-console"><Activity className="w-4 h-4 mr-2" /> Open Console</Button></Link>
        </div>
      </div>

      {/* Onboarding checklist (hidden when 100% done) */}
      {doneCount < steps.length && (
        <div className="rounded-xl border border-cyan-500/30 bg-gradient-to-br from-slate-900 to-cyan-950/20 p-5 mb-6" data-testid="onboarding-checklist">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <Sparkles className="w-4 h-4 text-cyan-signal" />
              <span className="font-mono text-xs uppercase tracking-widest text-cyan-signal">Getting started · {doneCount}/{steps.length}</span>
            </div>
            <Progress value={(doneCount / steps.length) * 100} className="w-24 h-1.5 bg-slate-800" />
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-2">
            {steps.map((s) => (
              <div key={s.label} data-testid={`onboarding-step-${s.label.split(' ')[0].toLowerCase()}`} className={`flex items-start gap-2 rounded-lg border p-3 text-sm ${s.done ? "border-emerald-500/30 bg-emerald-500/5" : "border-slate-800 bg-slate-950/60"}`}>
                {s.done ? <CheckCircle2 className="w-4 h-4 text-emerald-400 flex-shrink-0 mt-0.5" /> : <Circle className="w-4 h-4 text-slate-500 flex-shrink-0 mt-0.5" />}
                <div className="min-w-0 flex-1">
                  <p className={s.done ? "text-slate-500 line-through" : "text-slate-200"}>{s.label}</p>
                  {!s.done && (
                    s.to
                      ? <Link to={s.to} className="text-xs text-cyan-signal hover:underline mt-1 inline-block">{s.cta} →</Link>
                      : <button onClick={s.action} className="text-xs text-cyan-signal hover:underline mt-1 inline-block">{s.cta} →</button>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Credit + Usage chart */}
      <div className="grid md:grid-cols-3 gap-4 mb-8">
        <div className="md:col-span-2 rounded-xl border border-slate-800 bg-slate-900/60 p-6" data-testid="card-credits">
          <div className="flex items-center justify-between mb-2">
            <span className="font-mono text-xs uppercase tracking-widest text-slate-500">Credits Remaining</span>
            <span className="font-mono text-xs text-slate-500 uppercase">Plan: <span className="text-cyan-signal">{user.plan}</span></span>
          </div>
          <div className="flex items-baseline gap-2">
            <span className="text-4xl sm:text-5xl font-display font-bold tracking-tight text-cyan-signal">{(user.credits || 0).toLocaleString()}</span>
            <span className="text-slate-500 font-mono text-sm">/ {(user.credits_total || 0).toLocaleString()}</span>
          </div>
          <Progress value={creditsPct} className="mt-4 h-2 bg-slate-800" />
          <div className="mt-3 flex items-center justify-between text-xs">
            <span className="font-mono text-slate-500">Refresh: rollover ×3 months</span>
            <Link to="/pricing" className="text-cyan-signal hover:underline font-medium">Upgrade →</Link>
          </div>
        </div>
        <div className="rounded-xl border border-slate-800 bg-slate-900/60 p-6" data-testid="card-usage">
          <span className="font-mono text-xs uppercase tracking-widest text-slate-500">This account</span>
          <div className="mt-4 space-y-3">
            {["phone", "email", "ip"].map((k) => {
              const Icon = KIND_ICON[k];
              const val = stats?.by_kind?.[k]?.count || 0;
              return (
                <div key={k} className="flex items-center justify-between">
                  <span className="flex items-center gap-2 text-sm capitalize"><Icon className={`w-4 h-4 ${KIND_COLOR[k]}`} /> {k}</span>
                  <span className="font-mono text-sm text-slate-200">{val.toLocaleString()}</span>
                </div>
              );
            })}
            <div className="flex items-center justify-between pt-3 border-t border-slate-800">
              <span className="text-sm font-medium">Total</span>
              <span className="font-mono text-sm text-cyan-signal">{(stats?.total || 0).toLocaleString()}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Usage chart + Referral card */}
      <div className="grid lg:grid-cols-3 gap-4 mb-8">
        <div className="lg:col-span-2">
          <UsageChart />
        </div>
        <ReferralCard />
      </div>

      {/* API Keys */}
      <div className="rounded-xl border border-slate-800 bg-slate-900/60 p-6 mb-8" data-testid="card-api-keys">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="font-display font-semibold text-lg flex items-center gap-2"><KeyRound className="w-5 h-5 text-cyan-signal" /> API Keys</h3>
            <p className="text-sm text-slate-400 mt-0.5">Use as Bearer token or X-Api-Key header.</p>
          </div>
          <Button data-testid="button-api-key-generate" onClick={generateKey} disabled={generating} className="bg-cyan-signal text-slate-950 hover:bg-cyan-400 font-semibold">
            {generating ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Plus className="w-4 h-4 mr-1.5" /> Generate</>}
          </Button>
        </div>

        {newKey && (
          <div className="mb-4 rounded-lg border border-emerald-500/30 bg-emerald-500/5 p-4">
            <div className="text-xs font-mono uppercase tracking-widest text-emerald-400 mb-2">⚠ Copy this now — it won't be shown again</div>
            <div className="flex items-center gap-2">
              <code data-testid="new-api-key-value" className="flex-1 font-mono text-xs sm:text-sm text-emerald-300 bg-slate-950 px-3 py-2.5 rounded border border-slate-800 overflow-x-auto">{newKey}</code>
              <Button data-testid="button-api-key-copy" size="sm" onClick={() => copy(newKey)} variant="outline" className="border-slate-700 bg-slate-900 hover:bg-slate-800"><Copy className="w-4 h-4" /></Button>
            </div>
          </div>
        )}

        {(user.api_keys || []).length === 0 && !newKey && (
          <div className="text-center py-10 text-slate-500">
            <KeyRound className="w-8 h-8 mx-auto mb-3 opacity-40" />
            <p className="text-sm">No API keys yet. Generate one to start hitting the REST endpoints.</p>
          </div>
        )}

        <div className="space-y-2">
          {(user.api_keys || []).map((k) => (
            <div key={k.id} data-testid={`api-key-row-${k.id}`} className="flex items-center justify-between gap-3 rounded-lg border border-slate-800 bg-slate-950/60 px-4 py-3">
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2">
                  <span className="text-sm font-medium">{k.label}</span>
                  <code className="font-mono text-xs text-slate-400 truncate">{k.key_preview}</code>
                </div>
                <div className="text-xs font-mono text-slate-500 mt-0.5">created {new Date(k.created_at).toLocaleDateString()}</div>
              </div>
              <Button size="sm" variant="ghost" onClick={() => deleteKey(k.id)} className="text-slate-400 hover:text-rose-400 hover:bg-rose-500/10">
                <Trash2 className="w-4 h-4" />
              </Button>
            </div>
          ))}
        </div>
      </div>

      {/* Bulk CSV */}
      <div className="mb-8">
        <BulkCsv onDone={() => { refresh(); loadHistory(); }} />
      </div>

      {/* Recent Lookups */}
      <div className="rounded-xl border border-slate-800 bg-slate-900/60 p-6" data-testid="card-recent-lookups">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-display font-semibold text-lg flex items-center gap-2"><Zap className="w-5 h-5 text-cyan-signal" /> Recent Verifications</h3>
          <span className="text-xs font-mono text-slate-500">last 20</span>
        </div>
        {lookups.length === 0 ? (
          <div className="text-center py-10 text-slate-500 text-sm">No lookups yet. <Link to="/console" className="text-cyan-signal hover:underline">Run one →</Link></div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-xs font-mono uppercase tracking-widest text-slate-500 border-b border-slate-800">
                  <th className="py-2 text-left">Kind</th>
                  <th className="py-2 text-left">Input</th>
                  <th className="py-2 text-left hidden md:table-cell">Summary</th>
                  <th className="py-2 text-right">Credits</th>
                  <th className="py-2 text-right">When</th>
                </tr>
              </thead>
              <tbody>
                {lookups.map((l) => {
                  const Icon = KIND_ICON[l.kind] || Globe;
                  return (
                    <tr key={l._id} className="border-b border-slate-800/60 last:border-0 hover:bg-slate-950/40">
                      <td className="py-2.5 pr-3">
                        <span className={`inline-flex items-center gap-1.5 ${KIND_COLOR[l.kind]}`}>
                          <Icon className="w-3.5 h-3.5" /><span className="text-xs uppercase font-mono">{l.kind}</span>
                        </span>
                      </td>
                      <td className="py-2.5 pr-3 font-mono text-xs text-slate-200 truncate max-w-[160px]">{l.input}</td>
                      <td className="py-2.5 pr-3 text-xs text-slate-400 truncate max-w-[300px] hidden md:table-cell">{l.result_summary}</td>
                      <td className="py-2.5 pr-3 text-right font-mono text-xs">{l.credits_used}</td>
                      <td className="py-2.5 text-right font-mono text-xs text-slate-500">{new Date(l.timestamp).toLocaleTimeString()}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
